export type ApiErrorData = Record<string, unknown>;

export class ApiRequestError extends Error {
  code: string;
  status: number;
  data: ApiErrorData;
  constructor(code: string, message: string, status: number, data: ApiErrorData = {}) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

/**
 * ADR-0205 Amendment 3 — the hub's answer when a person opens something they
 * may not use: 403 `not_available`, naming only the thing asked for.
 */
export function isNotAvailable(e: unknown): boolean {
  return e instanceof ApiRequestError && e.code === "not_available";
}

// Set by App on mount so apiFetch can do a router-level redirect on session
// expiry instead of `location.assign`. The full-page-nav fallback below kicks
// in only if a 401 fires before App has mounted (effectively never in normal
// flow, but it keeps behavior safe during boot).
type UnauthorizedHandler = (loginPath: string) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

// The session cookie is browser-wide: selecting a tenant in another tab flips
// this tab's server-side context too. Each tab declares the tenant it believes
// it is in on every request (module state is per-tab); the hub rejects a
// mismatch with 409 tenant_context_changed before any wrong-tenant read/write,
// and the handler below lets TenantApp swap in a takeover screen.
let expectedTenantId: string | null = null;
export function setExpectedTenantId(tenantId: string | null): void {
  expectedTenantId = tenantId;
}

type TenantChangedHandler = (activeTenantName: string | null) => void;
let tenantChangedHandler: TenantChangedHandler | null = null;
export function setTenantChangedHandler(handler: TenantChangedHandler | null): void {
  tenantChangedHandler = handler;
}

function tenantHeader(): Record<string, string> {
  return expectedTenantId ? { "x-atlas-tenant-id": expectedTenantId } : {};
}

function handleTenantChanged(code: string, data: ApiErrorData | null): boolean {
  if (code !== "tenant_context_changed" || !tenantChangedHandler) return false;
  const name = typeof data?.active_tenant_name === "string" ? data.active_tenant_name : null;
  tenantChangedHandler(name);
  return true;
}

export async function apiFetch<T>(
  path: string,
  opts?: {
    method?: string;
    body?: unknown;
    headers?: Record<string, string>;
    /**
     * Optional AbortSignal. When supplied, the underlying `fetch` is aborted
     * on signal and the returned promise rejects with the native `DOMException`
     * (`name === "AbortError"`). Used by ADR-0056 reactive viz where a newer
     * slicer change supersedes an in-flight request.
     */
    signal?: AbortSignal;
  }
): Promise<T> {
  const headers: Record<string, string> = { ...tenantHeader(), ...(opts?.headers ?? {}) };
  const hasBody = opts?.body !== undefined;
  if (hasBody) headers["content-type"] = "application/json";

  const res = await fetch(path, {
    method: opts?.method ?? "GET",
    headers,
    credentials: "include",
    body: hasBody ? JSON.stringify(opts.body) : undefined,
    signal: opts?.signal
  });

  if (res.status === 204) {
    return undefined as T;
  }

  // Session expired mid-page → bounce to login with a return path so the
  // user lands back where they were after re-auth. /api/auth/* endpoints
  // legitimately expect 401s (the auth probe, wrong-password on login) and
  // handle them inline, so they're excluded.
  if (res.status === 401 && !path.startsWith("/api/auth/") && location.pathname !== "/login") {
    const current = location.pathname + location.search;
    const loginPath = `/login?redirect=${encodeURIComponent(current)}`;
    if (unauthorizedHandler) {
      unauthorizedHandler(loginPath);
    } else {
      location.assign(loginPath);
    }
    return new Promise<T>(() => {});
  }

  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as ApiErrorData | null;
    const nestedError = data?.error;
    const code =
      (typeof nestedError === "object" && nestedError && typeof (nestedError as { code?: unknown }).code === "string"
        ? ((nestedError as { code: string }).code)
        : (typeof nestedError === "string" ? nestedError : undefined)) ?? `http_${res.status}`;
    const msg =
      (typeof nestedError === "object" && nestedError && typeof (nestedError as { message?: unknown }).message === "string"
        ? ((nestedError as { message: string }).message)
        : (typeof data?.message === "string" ? data.message : undefined)) ?? code;
    if (handleTenantChanged(code, data)) return new Promise<T>(() => {});
    throw new ApiRequestError(code, msg, res.status, data ?? {});
  }

  return (await res.json()) as T;
}

/**
 * Binary-response variant of `apiFetch`. POSTs JSON, expects a binary payload
 * back. Used for server-side file downloads (ADR-0099 page-export endpoints).
 * Returns the response `Blob` + a suggested filename parsed from the
 * `Content-Disposition` header (best-effort).
 *
 * Same auth + 401-redirect behavior as `apiFetch`; error responses (non-2xx)
 * are parsed as JSON and rethrown as `ApiRequestError`.
 */
export async function apiFetchBlob(
  path: string,
  opts?: {
    method?: string;
    body?: unknown;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }
): Promise<{ blob: Blob; filename: string | null; contentType: string }> {
  const headers: Record<string, string> = { ...tenantHeader(), ...(opts?.headers ?? {}) };
  const hasBody = opts?.body !== undefined;
  if (hasBody) headers["content-type"] = "application/json";

  const res = await fetch(path, {
    method: opts?.method ?? "POST",
    headers,
    credentials: "include",
    body: hasBody ? JSON.stringify(opts!.body) : undefined,
    signal: opts?.signal,
  });

  if (res.status === 401 && !path.startsWith("/api/auth/") && location.pathname !== "/login") {
    const current = location.pathname + location.search;
    const loginPath = `/login?redirect=${encodeURIComponent(current)}`;
    if (unauthorizedHandler) {
      unauthorizedHandler(loginPath);
    } else {
      location.assign(loginPath);
    }
    return new Promise(() => {});
  }

  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as ApiErrorData | null;
    const nestedError = data?.error;
    const code =
      (typeof nestedError === "object" && nestedError && typeof (nestedError as { code?: unknown }).code === "string"
        ? ((nestedError as { code: string }).code)
        : (typeof nestedError === "string" ? nestedError : undefined)) ?? `http_${res.status}`;
    const msg =
      (typeof nestedError === "object" && nestedError && typeof (nestedError as { message?: unknown }).message === "string"
        ? ((nestedError as { message: string }).message)
        : (typeof data?.message === "string" ? data.message : undefined)) ?? code;
    if (handleTenantChanged(code, data)) return new Promise(() => {});
    throw new ApiRequestError(code, msg, res.status, data ?? {});
  }

  const contentType = res.headers.get("Content-Type") ?? "application/octet-stream";
  const disposition = res.headers.get("Content-Disposition");
  let filename: string | null = null;
  if (disposition) {
    const m = /filename="?([^";]+)"?/i.exec(disposition);
    if (m) filename = m[1];
  }
  const blob = await res.blob();
  return { blob, filename, contentType };
}

/**
 * Trigger a download from a `Blob` via the standard anchor-click pattern.
 * Used after `apiFetchBlob` to save the server-built file.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Multipart variant of `apiFetch` for file uploads.
 *
 * ADR-0092 — the upload-sniff endpoint streams a file via multipart/form-data.
 * `apiFetch` always JSON-encodes the body, so multipart needs its own path.
 * Shape mirrors `apiFetch` (same credentials, same error mapping, same 401
 * redirect-to-login behavior).
 */
export async function apiFetchMultipart<T>(
  path: string,
  form: FormData,
  opts?: { signal?: AbortSignal },
): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: tenantHeader(),
    credentials: "include",
    body: form,
    signal: opts?.signal,
    // NB: do NOT set Content-Type — the browser fills in the multipart boundary.
  });

  if (res.status === 204) return undefined as T;

  if (res.status === 401 && !path.startsWith("/api/auth/") && location.pathname !== "/login") {
    const current = location.pathname + location.search;
    const loginPath = `/login?redirect=${encodeURIComponent(current)}`;
    if (unauthorizedHandler) {
      unauthorizedHandler(loginPath);
    } else {
      location.assign(loginPath);
    }
    return new Promise<T>(() => {});
  }

  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as ApiErrorData | null;
    const nestedError = data?.error;
    const code =
      (typeof nestedError === "object" && nestedError && typeof (nestedError as { code?: unknown }).code === "string"
        ? ((nestedError as { code: string }).code)
        : (typeof nestedError === "string" ? nestedError : undefined)) ?? `http_${res.status}`;
    const msg =
      (typeof nestedError === "object" && nestedError && typeof (nestedError as { message?: unknown }).message === "string"
        ? ((nestedError as { message: string }).message)
        : (typeof data?.message === "string" ? data.message : undefined)) ?? code;
    if (handleTenantChanged(code, data)) return new Promise<T>(() => {});
    throw new ApiRequestError(code, msg, res.status, data ?? {});
  }

  return (await res.json()) as T;
}

/**
 * Upload-with-progress variant of `apiFetchMultipart`.
 *
 * ## Why this exists rather than another `fetch`
 *
 * `fetch` cannot report how much of a request BODY has gone out. On a 95 MB
 * CSV that is the whole of what a person needs to see, and its absence is half
 * of the 2026-09-12 defect: the file went, nothing on screen moved, and the
 * reader had no way to tell a working upload from a broken one.
 * `XMLHttpRequest` still exposes `upload.onprogress`, so the one thing the
 * modern API cannot do is the one thing this needs.
 *
 * Everything else is deliberately `apiFetchMultipart`'s behaviour, because two
 * upload helpers that disagree about auth or errors is how a surface ends up
 * refusing in one place and hanging in another:
 *
 * - `withCredentials` is the cookie session, same as `credentials: "include"`.
 * - The tab's tenant header rides along, so a tenant switched in another tab is
 *   caught by the hub's 409 before any bytes are written.
 * - Non-2xx bodies are mapped to `ApiRequestError` with the same code/message
 *   extraction, so `catch (e) { setError(e.message) }` reads the same text.
 * - A 401 redirects to login with a return path; a `tenant_context_changed`
 *   hands off to the takeover screen.
 *
 * `onProgress` reports bytes ON THE WIRE. Where the caller compressed first,
 * that is the compressed size and the caller is the one that knows it — which
 * is why the total comes from the event rather than from the original file.
 */
export async function apiUploadMultipart<T>(
  path: string,
  form: FormData,
  opts?: {
    signal?: AbortSignal;
    /** Called as the request body goes out. `total` is 0 while the length is unknown. */
    onProgress?: (loaded: number, total: number) => void;
  },
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path, true);
    xhr.withCredentials = true;
    for (const [k, v] of Object.entries(tenantHeader())) xhr.setRequestHeader(k, v);
    // NB: no Content-Type — the browser fills in the multipart boundary.

    if (opts?.onProgress) {
      xhr.upload.onprogress = (e) => {
        opts.onProgress?.(e.loaded, e.lengthComputable ? e.total : 0);
      };
    }

    const abort = () => xhr.abort();
    if (opts?.signal) {
      if (opts.signal.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      opts.signal.addEventListener("abort", abort, { once: true });
    }
    const cleanup = () => opts?.signal?.removeEventListener("abort", abort);

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    // A dropped connection mid-upload lands here with no status and no body.
    // It must read as a transfer failure a person can act on — the 2026-06-19
    // `"fetch failed"` lesson, which said nothing and sent everyone hunting in
    // the wrong place.
    xhr.onerror = () => {
      cleanup();
      reject(
        new ApiRequestError(
          "network_error",
          "The upload did not finish — the connection dropped on the way. Nothing was stored. Try again.",
          0,
        ),
      );
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(new ApiRequestError("timeout", "The upload timed out before it finished. Nothing was stored. Try again.", 0));
    };

    xhr.onload = () => {
      cleanup();
      const status = xhr.status;

      if (status === 204) {
        resolve(undefined as T);
        return;
      }

      if (status === 401 && !path.startsWith("/api/auth/") && location.pathname !== "/login") {
        const current = location.pathname + location.search;
        const loginPath = `/login?redirect=${encodeURIComponent(current)}`;
        if (unauthorizedHandler) {
          unauthorizedHandler(loginPath);
        } else {
          location.assign(loginPath);
        }
        return; // never settles, matching apiFetch — the page is navigating away
      }

      let parsed: unknown = null;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        parsed = null;
      }

      if (status < 200 || status >= 300) {
        const data = (parsed ?? null) as ApiErrorData | null;
        const { code, msg } = readApiError(data, status);
        if (handleTenantChanged(code, data)) return; // the takeover screen owns the page now
        reject(new ApiRequestError(code, msg, status, data ?? {}));
        return;
      }

      resolve(parsed as T);
    };

    xhr.send(form);
  });
}

/**
 * The error code + message an Atlas response carries, from the one shape every
 * route answers with (`{ error, message }`, occasionally nested).
 *
 * Extracted so `apiUploadMultipart` cannot drift from `apiFetchMultipart`: the
 * rule for reading a refusal is the same rule whichever transport carried it,
 * and a second copy of it is how one surface starts showing `http_413` where
 * another shows the sentence the hub actually wrote.
 */
function readApiError(data: ApiErrorData | null, status: number): { code: string; msg: string } {
  const nestedError = data?.error;
  const code =
    (typeof nestedError === "object" && nestedError && typeof (nestedError as { code?: unknown }).code === "string"
      ? (nestedError as { code: string }).code
      : typeof nestedError === "string"
        ? nestedError
        : undefined) ?? `http_${status}`;
  const msg =
    (typeof nestedError === "object" && nestedError && typeof (nestedError as { message?: unknown }).message === "string"
      ? (nestedError as { message: string }).message
      : typeof data?.message === "string"
        ? data.message
        : undefined) ?? code;
  return { code, msg };
}
